import { UserDetailService } from './shared/services/user-detail.service';
import { login } from './features/auth/state/auth.action';
import { Router, NavigationEnd } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, tap, take, distinctUntilChanged, filter } from 'rxjs';
import { NavHeaderService } from './shared/services/nav-header.service';
import { Component, inject, OnInit } from '@angular/core';
import { AppState } from './state/app.reducer';
import { KeyboardInsetService } from './shared/services/keyboard-inset.service';
import { isLoggedIn, isLoggedOut } from './features/auth/state/auth.selector';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'ActivityPro: App for Club & Coaching Management';
  isLoggedIn$!: Observable<boolean>;
  isLoggedOut$!: Observable<boolean>;
  private hasNavigated = false;
  private readonly keyboardInsetService = inject(KeyboardInsetService);

  constructor(
    private navHeaderService: NavHeaderService,
    private store: Store<AppState>,
    private route: Router,
    private userDetailService: UserDetailService
  ) { }

  ngOnInit() {
    this.keyboardInsetService.initialize();

    const userProfile = localStorage.getItem('USER');
    const superUserDetails = localStorage.getItem('superUser');
    const hasSelectedClub = localStorage.getItem('postGreParentClubId') !== null;

    if (superUserDetails == 'true') {
      this.userDetailService.isSuperUserLoggedIn.next(true);
    }

    if (userProfile == undefined) {
      localStorage.removeItem('USER');
    }

    // Only navigate on app initialization if user is already logged in
    // Don't navigate on subsequent login state changes (let login component handle that)
    if (userProfile) {
      let user = JSON.parse(userProfile);
      this.userDetailService.setUserDetails(user);
      this.store.dispatch(login({ authUser: user }));

      // Navigate based on user role and permissions (only on app init/refresh)
      this.navHeaderService.toggleHeaderVisibility(true);

      // Check if we're on the root path or auth path, then redirect appropriately
      const currentPath = window.location.pathname;
      const isAuthPath = currentPath === '/' || currentPath === '' || currentPath.startsWith('/auth') || currentPath.includes('/auth/');

      if (isAuthPath) {
        // Super user who hasn't selected a club - go to super user dashboard
        if (superUserDetails === 'true' && !hasSelectedClub) {
          this.route.navigateByUrl('/main/dashboard/superUserDashboardComponent');
        }
        // Super user who has selected a club OR any valid staff role - go to dashboard
        else if (['2','4','6','20','30','31','32'].includes(String(user.roleType)) || (superUserDetails === 'true' && hasSelectedClub)) {
          this.route.navigateByUrl('/main/dashboard');
        }
        // Non-staff with booking permission - go to court booking
        else if (user.isAllowForBooking) {
          this.route.navigateByUrl('/main/court-booking');
        }
        // No permissions - go to no access
        else {
          this.route.navigateByUrl('/main/dashboard/no-access');
        }
      }
      // If already on a valid route, don't navigate
    }

    // Subscribe to login state for UI updates only (no navigation)
    this.isLoggedIn$ = this.store.pipe(
      select(isLoggedIn),
      distinctUntilChanged(),
      tap((isLoggedIn) => {
        if (this.hasNavigated) {
          return;
        }

        const currentUrl = this.route.url;
        const shouldRedirectToDashboard = currentUrl === '/' || currentUrl.startsWith('/auth') || currentUrl === '';

        if (isLoggedIn) {
          this.navHeaderService.toggleHeaderVisibility(true);
        } else {
          this.navHeaderService.toggleHeaderVisibility(false);
        }
        return isLoggedIn;
      })
    );

    this.isLoggedIn$.subscribe();
  }
}